<template>
  <div class="page-container recruitment-detail-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ t('recruitment.detail_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ t('menu.home') }}</router-link> /
          <router-link to="/recruitment">{{ t('recruitment.list_title') }}</router-link> /
          <span>{{ t('recruitment.detail_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：招聘详情 -->
        <el-col :span="24" :xs="24">
          <div class="detail-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="10" animated />
            </div>
            <div v-else-if="!recruitmentDetail" class="empty-data">
              <el-empty :description="t('recruitment.no_data')" />
            </div>
            <div v-else class="recruitment-article">
              <h2 class="article-title">{{ recruitmentDetail.page_name }}</h2>
              <div class="article-meta">
                <span>{{ t('recruitment.create_time') }}: {{ formatTime(recruitmentDetail.create_time) }}</span>
                <span v-if="recruitmentDetail.sort === 99" class="top-tag">
                  {{ t('recruitment.top') }}
                </span>
                <span v-if="recruitmentDetail.page_type !== 1">
                  {{ t(`recruitment.type_${recruitmentDetail.page_type}`) }}
                </span>
              </div>

<!--              &lt;!&ndash; SEO信息（可选展示） &ndash;&gt;-->
<!--              <div v-if="recruitmentDetail.seo_title || recruitmentDetail.seo_description" class="article-seo">-->
<!--                <div v-if="recruitmentDetail.seo_title" class="seo-title">-->
<!--                  <strong>{{ t('recruitment.seo_title') }}:</strong> {{ recruitmentDetail.seo_title }}-->
<!--                </div>-->
<!--                <div v-if="recruitmentDetail.seo_description" class="seo-desc">-->
<!--                  <strong>{{ t('recruitment.seo_description') }}:</strong> {{ recruitmentDetail.seo_description }}-->
<!--                </div>-->
<!--              </div>-->

              <!-- 招聘内容（JSON解析后渲染） -->
              <div class="article-content" v-html="renderContent(recruitmentDetail.content)"></div>

            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { ElMessage } from 'element-plus';
import { getRecruitmentDetail } from '@/assets/api/website';

// -------------------------- 类型定义 --------------------------
/** 招聘详情类型接口 */
interface RecruitmentDetail {
  id: number;
  page_type: number;
  page_name: string;
  content: Record<string, any> | string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
  sort: number;
  status: number;
  create_time: string | Date;
  update_time: string | Date;
}

// -------------------------- 状态管理 --------------------------
const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

const loading = ref(true);
const recruitmentDetail = ref<RecruitmentDetail | null>(null);

// -------------------------- 方法定义 --------------------------
/** 格式化时间 */
const formatTime = (time: string | Date) => {
  if (!time) return '';
  try {
    return format(new Date(time), 'yyyy-MM-dd');
  } catch (e) {
    return time.toString();
  }
};

/** 渲染招聘内容（处理JSON格式的内容） */
const renderContent = (content: Record<string, any> | string | null) => {
  console.log('content:', content)
  content = content.richtext;
  if (!content) return '<div class="empty-content">暂无招聘内容</div>';

  // 如果是字符串，直接返回
  if (typeof content === 'string') {
    return content;
  }

  // 如果是JSON对象，解析并生成HTML
  let html = '';

  // 处理职位名称
  if (content.position_name) {
    html += `<h3>${t('recruitment.position_name')}: ${content.position_name}</h3>`;
  }

  // 处理岗位职责
  if (content.job_content) {
    html += `
      <div class="job-section">
        <h4>${t('recruitment.job_content')}:</h4>
        <ul>${Array.isArray(content.job_content)
      ? content.job_content.map(item => `<li>${item}</li>`).join('')
      : `<li>${content.job_content}</li>`}</ul>
      </div>
    `;
  }

  // 处理任职要求
  if (content.job_requirement) {
    html += `
      <div class="job-section">
        <h4>${t('recruitment.job_requirement')}:</h4>
        <ul>${Array.isArray(content.job_requirement)
      ? content.job_requirement.map(item => `<li>${item}</li>`).join('')
      : `<li>${content.job_requirement}</li>`}</ul>
      </div>
    `;
  }

  // 处理联系方式
  if (content.contact_info) {
    html += `
      <div class="job-section contact-section">
        <h4>${t('recruitment.contact_info')}:</h4>
        <p>${content.contact_info}</p>
      </div>
    `;
  }

  // 如果没有结构化数据，显示原始内容
  if (html === '') {
    html = `<pre>${JSON.stringify(content, null, 2)}</pre>`;
  }

  return html;
};

/** 获取招聘详情数据 */
const fetchRecruitmentDetail = async (id: number) => {
  if (!id) return;

  loading.value = true;
  try {
    const res = await getRecruitmentDetail(id, {
      lang: locale.value,
    });

    if (res) {
      recruitmentDetail.value = res;
    } else {
      ElMessage.warning(t('recruitment.no_data'));
    }
  } catch (error) {
    console.error("获取招聘详情失败:", error);
    ElMessage.error(t('recruitment.fetch_error'));
  } finally {
    loading.value = false;
  }
};

// -------------------------- 监听与初始化 --------------------------
// 监听路由参数变化
watch(
  () => route.params.id,
  (newId) => {
    const recruitmentId = parseInt(newId as string);
    if (recruitmentId) {
      fetchRecruitmentDetail(recruitmentId);
    } else {
      loading.value = false;
      ElMessage.warning(t('recruitment.invalid_id'));
      router.push('/recruitment');
    }
  },
  { immediate: true }
);

// 监听语言变化，重新加载数据
watch(
  () => locale.value,
  () => {
    const recruitmentId = parseInt(route.params.recruitment_id as string);
    if (recruitmentId) {
      fetchRecruitmentDetail(recruitmentId);
    }
  }
);
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.recruitment-detail-page {
  .page-header {
    padding: 100px 0 40px;
    background:
      linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%),
      url('@/assets/img/header.jpg') center center / cover no-repeat;
    position: relative;
    border-bottom: 1px solid rgba($primary-color, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      filter: blur(2px);
      z-index: -1;
    }

    h1 {
      font-family: $font-serif;
      font-size: 2.5rem;
      font-weight: 700;
      color: $primary-color-2;
      margin-bottom: 20px;
      letter-spacing: 5px;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
      position: relative;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;
      position: relative;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
  }

  .detail-wrapper {
    min-height: 600px;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  .recruitment-article {
    .article-title {
      font-size: 28px;
      color: #333;
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      letter-spacing: 1px;
      border-bottom: 1px solid #eee;
    }

    .article-meta {
      display: flex;
      justify-content: center;
      gap: 20px;
      font-size: 14px;
      color: #999;
      margin-bottom: 30px;
      flex-wrap: wrap;

      .top-tag {
        color: #e6a23c;
        font-weight: bold;
        background: rgba(230, 162, 60, 0.1);
        padding: 2px 8px;
        border-radius: 4px;
      }
    }

    .article-seo {
      background-color: #f9f9f9;
      padding: 15px;
      border-left: 4px solid $primary-color;
      margin-bottom: 30px;
      line-height: 1.8;
      color: #666;

      .seo-title, .seo-desc {
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .article-content {
      line-height: 1.8;
      color: #333;
      font-size: 16px;
      padding: 0 10px;
      margin-bottom: 40px;

      :deep(*) {
        box-sizing: border-box;
      }

      :deep(p) {
        margin-bottom: 15px;
        text-align: justify;
      }

      :deep(h3) {
        font-size: 20px;
        color: $primary-color;
        margin: 25px 0 15px;
        padding-bottom: 5px;
        border-bottom: 1px solid #eee;
      }

      :deep(h4) {
        font-size: 18px;
        color: #444;
        margin: 20px 0 10px;
      }

      :deep(ul) {
        margin-bottom: 15px;
        padding-left: 25px;
      }

      :deep(li) {
        margin-bottom: 8px;
        position: relative;

        &::before {
          content: "•";
          color: $primary-color;
          position: absolute;
          left: -15px;
        }
      }

      :deep(.job-section) {
        margin-bottom: 25px;
        padding: 15px;
        background-color: #fafafa;
        border-radius: 8px;

        &.contact-section {
          background-color: rgba($primary-color, 0.05);
          border: 1px solid rgba($primary-color, 0.1);
        }
      }

      :deep(.empty-content) {
        text-align: center;
        color: #999;
        padding: 40px 0;
      }

      :deep(pre) {
        background-color: #f5f5f5;
        padding: 15px;
        border-radius: 8px;
        overflow-x: auto;
        font-size: 14px;
        color: #666;
      }
    }

    .back-link {
      text-align: center;
    }
  }
}

// 响应式调整
@media (max-width: 768px) {
  .page-header {
    padding: 80px 0 20px;
  }

  .page-content {
    padding: 20px;
  }

  .detail-wrapper {
    min-height: 400px;
    padding: 15px;
  }

  .article-title {
    font-size: 24px !important;
  }

  .article-meta {
    flex-direction: column;
    align-items: center;
    gap: 8px !important;
  }
}
</style>